import {
  type ClassSeason,
  type EventKind,
  SEASONS,
  SEASON_ROLES,
  type SeasonRole,
  type SeasonStory,
  seasonStory,
  useClassSeason,
  useSeasonActions,
  useSeasonMarks,
  useSeasonProgress,
} from '@shkola/api';
import type { SubjectIcon } from '@shkola/design';
import { RocketLaunchIcon } from '@phosphor-icons/react/RocketLaunch';
import { Link } from '@tanstack/react-router';
import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentContext } from '../app/ViewContext';
import {
  Block,
  DARK,
  DEEP,
  EventArt,
  GRAD,
  type Grad,
  Head,
  INK,
  INK_FAINT,
  INK_SOFT,
  LINE,
  PAPER,
  PageHead,
  PillIcon,
  SubjectArt,
  TaskArt,
  grad,
  pillClass,
  ringButton,
} from '../design/forms';
import { ArrowUpRightIcon, CaretRightIcon, CheckCircleIcon, CheckIcon } from '../design/icons';
import type { TaskArtKind } from './home/LineArt';
import { Label, LifeArt } from './LifeArt';

/*
 * «Сезон класу» in «Форми 2» (28.10). The story's name for the title, the station and the intro under it;
 * the way as a track of stops — the passed ones lime with a tick, the class's stop with its picture and
 * the rocket over it, the coming ones on the paper with their number. Then the mission of the station on
 * a soft card with the station's picture big and quiet behind, the four teams' tasks under it (one's own
 * team on lime), the button «я зробив(-ла)»; at the side one's role on its colour and how the teams go.
 * The emoji of the stories and the roles are drawn as our pictures.
 */

type Art =
  | { subject: SubjectIcon; tone: Grad }
  | { event: EventKind; tone: Grad }
  | { task: TaskArtKind; tone: Grad };

/** The stations' pictures by their emoji (the stories keep the emoji as data). */
const STATION_ART: Record<string, Art> = {
  '🌍': { subject: 'Earth', tone: 'sky' },
  '🛰': { subject: 'Atom', tone: 'cobalt' },
  '🌙': { subject: 'Telescope', tone: 'lavender' },
  '☄': { event: 'other', tone: 'apricot' },
  '📡': { task: 'ack', tone: 'pink' },
  '☀': { event: 'holiday', tone: 'butter' },
  '🔴': { subject: 'Earth', tone: 'coral' },
  '🏁': { task: 'catchUp', tone: 'lime' },
};
const FALLBACK: Art = { event: 'other', tone: 'lavender' };
const stationArt = (emoji: string) => STATION_ART[emoji.replace(/️/g, '')] ?? FALLBACK;

/** The teams: a picture and a colour each. */
const ROLE_ART: Record<SeasonRole, { subject: SubjectIcon; tone: Grad }> = {
  engineer: { subject: 'Hammer', tone: 'apricot' },
  scientist: { subject: 'FlaskConical', tone: 'sky' },
  navigator: { subject: 'Map', tone: 'mint' },
  chronicler: { subject: 'PenLine', tone: 'pink' },
};

const roleOf = (key: SeasonRole) => SEASON_ROLES.find((r) => r.key === key);

function Pic({
  art,
  tone,
  size,
  ghost,
  className,
}: {
  art: Art;
  tone?: Grad;
  size: number;
  ghost?: boolean;
  className?: string;
}) {
  const t = tone ?? art.tone;
  if ('subject' in art)
    return (
      <SubjectArt icon={art.subject} tone={t} size={size} ghost={ghost} className={className} />
    );
  if ('event' in art)
    return <EventArt kind={art.event} tone={t} size={size} ghost={ghost} className={className} />;
  return <TaskArt kind={art.task} tone={t} size={size} ghost={ghost} className={className} />;
}

/** The way through the stations: the passed ones with a tick, the class's stop with the rocket. */
export function SeasonMap({ story, stage }: { story: SeasonStory; stage: number }) {
  const last = story.stations.length - 1;
  // on a narrow screen the way scrolls: the class's stop in the middle of it
  const track = useRef<HTMLOListElement>(null);
  useLayoutEffect(() => {
    const ol = track.current;
    const here = ol?.children[Math.min(stage, last)] as HTMLElement | undefined;
    if (ol && here && ol.scrollWidth > ol.clientWidth)
      ol.scrollLeft = here.offsetLeft - (ol.clientWidth - here.offsetWidth) / 2;
  }, [stage, last]);
  return (
    <ol
      ref={track}
      className="no-scrollbar relative -mx-1 flex items-start overflow-x-auto px-1 pt-2 pb-1"
    >
      {story.stations.map((s, i) => {
        const here = i === stage;
        const passed = i < stage;
        const art = stationArt(s.emoji);
        const segment = (on: boolean, hidden: boolean) => (
          <span
            className={`h-0 flex-1 ${hidden ? 'invisible' : ''}`}
            style={{
              borderTop: on ? `2px solid ${DARK}` : `2px dashed ${LINE}`,
            }}
          />
        );
        return (
          <li key={s.title} className="flex min-w-[88px] flex-1 flex-col items-center gap-2">
            <span className="flex h-11 items-end">
              {here && <LifeArt kind="rocket" tone="lavender" size={40} className="motion-pop" />}
            </span>
            <span className="flex h-16 w-full items-center">
              {segment(passed || here, i === 0)}
              {here ? (
                <span
                  className="flex size-16 shrink-0 items-center justify-center rounded-full"
                  style={{ backgroundImage: grad(art.tone), boxShadow: `inset 0 0 0 2px ${DARK}` }}
                >
                  <Pic art={art} size={44} />
                </span>
              ) : passed ? (
                <span
                  className="flex size-11 shrink-0 items-center justify-center rounded-full"
                  style={{ backgroundImage: grad('lime'), color: GRAD.lime.ink }}
                >
                  <CheckIcon size={18} weight="bold" aria-hidden />
                </span>
              ) : (
                <span
                  className="flex size-11 shrink-0 items-center justify-center rounded-full text-[15px] font-medium"
                  style={{ backgroundColor: PAPER, color: INK_FAINT }}
                >
                  {i + 1}
                </span>
              )}
              {segment(passed, i === last)}
            </span>
            <span
              className={`px-1 text-center text-[12.5px] leading-4 ${here ? 'font-medium' : ''}`}
              style={{ color: here ? INK : passed ? INK_SOFT : INK_FAINT }}
            >
              {s.title}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function Teams({ seasonId }: { seasonId: string }) {
  const { t } = useTranslation();
  const progress = useSeasonProgress(seasonId).data ?? [];
  const done = progress.reduce((n, p) => n + p.done, 0);
  const total = progress.reduce((n, p) => n + p.members, 0);
  const bar = (value: number, of: number, tone: Grad, height: string) => (
    <span
      className={`block overflow-hidden rounded-full ${height}`}
      style={{ backgroundColor: PAPER }}
    >
      <span
        className="block h-full rounded-full transition-[width] duration-500"
        style={{
          width: of ? `${(100 * value) / of}%` : 0,
          backgroundImage: `linear-gradient(90deg, ${DEEP[tone][0]} 0%, ${DEEP[tone][1]} 100%)`,
        }}
      />
    </span>
  );
  return (
    <Block>
      <Head title={t('season.team')} />
      <p
        className="font-display text-[44px] leading-[48px] font-light tracking-[-0.04em]"
        style={{ color: INK }}
      >
        {done}
        <span className="text-[24px]" style={{ color: INK_FAINT }}>
          {' '}
          / {total}
        </span>
      </p>
      <p className="mb-3 text-[13.5px]" style={{ color: INK_SOFT }}>
        {t('season.progress', { done, total })}
      </p>
      {bar(done, total, 'lime', 'h-2.5')}
      <ul className="mt-4 flex flex-col">
        {progress.map((p) => {
          const r = roleOf(p.role);
          const art = ROLE_ART[p.role];
          return (
            <li
              key={p.role}
              className="flex items-center gap-3 py-2.5"
              style={{ borderTop: `1px solid ${LINE}` }}
            >
              <SubjectArt icon={art.subject} tone={art.tone} size={34} />
              <span className="min-w-0 flex-1">
                <span className="flex items-baseline justify-between gap-2">
                  <span className="text-[14.5px] font-medium" style={{ color: INK }}>
                    {r?.name}
                  </span>
                  <span className="text-[13px]" style={{ color: INK_SOFT }}>
                    {p.done} / {p.members}
                  </span>
                </span>
                <span className="mt-1.5 block">{bar(p.done, p.members, art.tone, 'h-1.5')}</span>
              </span>
            </li>
          );
        })}
      </ul>
      <p className="mt-3 text-[13px] leading-[18px]" style={{ color: INK_FAINT }}>
        {t('season.rule')}
      </p>
    </Block>
  );
}

function Running({
  season,
  story,
  lead,
  classId,
}: {
  season: ClassSeason;
  story: SeasonStory;
  lead: boolean;
  classId: string;
}) {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const student =
    ctx.kind === 'student' || ctx.kind === 'guardian' ? ctx.child.memberId : undefined;
  const role = student ? season.roles[student] : undefined;
  const marks = useSeasonMarks(season.id, season.stage);
  const actions = useSeasonActions(classId);
  const [moved, setMoved] = useState(false);
  const station = story.stations[Math.min(season.stage, story.stations.length - 1)];
  const mine = student !== undefined && (marks.data?.includes(student) ?? false);
  const art = station ? stationArt(station.emoji) : FALLBACK;
  const r = role ? roleOf(role) : undefined;

  return (
    <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
      <Block>
        {season.finished ? (
          <div className="flex flex-col items-center py-10 text-center">
            <TaskArt kind="catchUp" tone="lime" size={96} />
            <p className="mt-3 font-display text-[32px] font-light" style={{ color: INK }}>
              {t('season.finished')}
            </p>
            <p className="mt-1 text-[15px]" style={{ color: INK_SOFT }}>
              {t('season.finishedHint')}
            </p>
          </div>
        ) : (
          station && (
            <>
              {/* the mission on its station's colour, the picture big and quiet behind */}
              <div
                className="relative isolate overflow-hidden rounded-[24px] p-5 md:p-6"
                style={{ backgroundImage: grad(art.tone), color: GRAD[art.tone].ink }}
              >
                <Pic
                  art={art}
                  size={176}
                  ghost
                  className="pointer-events-none absolute -top-5 -right-4 -z-10"
                />
                <p className="text-[13px] font-medium">{t('season.mission')}</p>
                <h2
                  className="mt-1 font-display text-[36px] leading-[42px] font-light tracking-[-0.035em]"
                  style={{ color: INK }}
                >
                  {station.title}
                </h2>
                <p className="mt-2 max-w-xl text-[15px] leading-[23px]" style={{ color: INK }}>
                  {station.story}
                </p>
              </div>
              <ul className="mt-2.5 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {SEASON_ROLES.map((x) => {
                  const task = station.tasks[x.key];
                  const ours = x.key === role;
                  const ra = ROLE_ART[x.key];
                  return (
                    <li
                      key={x.key}
                      className="flex flex-col gap-2.5 rounded-[20px] p-4"
                      style={
                        ours
                          ? { backgroundImage: grad('lime'), color: GRAD.lime.ink }
                          : { backgroundColor: PAPER, color: INK_SOFT }
                      }
                    >
                      <span className="flex items-center gap-2.5">
                        <SubjectArt icon={ra.subject} tone={ours ? 'lime' : ra.tone} size={34} />
                        <span className="min-w-0 flex-1">
                          <span
                            className="block text-[14.5px] leading-[18px] font-medium"
                            style={{ color: INK }}
                          >
                            {x.name}
                          </span>
                          <span className="block text-[12.5px]">{task.subject}</span>
                        </span>
                        {ours && (
                          <Label tone="lime" white>
                            {t('season.yours')}
                          </Label>
                        )}
                      </span>
                      <span className="text-[15px] leading-[22px]" style={{ color: INK }}>
                        {task.text}
                      </span>
                    </li>
                  );
                })}
              </ul>
              {(ctx.kind === 'student' || lead || moved) && (
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {ctx.kind === 'student' &&
                    (mine ? (
                      <span
                        className="inline-flex h-11 items-center gap-2 rounded-[12px] px-3.5 text-[14px] font-medium"
                        style={{ backgroundImage: grad('mint'), color: GRAD.mint.ink }}
                      >
                        <CheckCircleIcon size={18} weight="fill" aria-hidden />
                        {t('season.doneAlready')}
                      </span>
                    ) : (
                      <button
                        type="button"
                        disabled={actions.done.isPending}
                        onClick={() =>
                          void actions.done.mutateAsync(season.id).then((x) => setMoved(x.moved))
                        }
                        className={`${pillClass('dark')} disabled:opacity-35`}
                      >
                        {t('season.done')}
                        <PillIcon icon={CheckIcon} />
                      </button>
                    ))}
                  {lead && (
                    <button
                      type="button"
                      disabled={actions.next.isPending}
                      onClick={() => actions.next.mutate(season.id)}
                      className={`${pillClass('light')} disabled:opacity-35`}
                    >
                      {t('season.next')}
                      <PillIcon icon={CaretRightIcon} />
                    </button>
                  )}
                  {moved && (
                    <span
                      className="inline-flex items-center gap-2 text-[15px] font-medium"
                      style={{ color: GRAD.lavender.ink }}
                    >
                      <RocketLaunchIcon size={18} weight="fill" aria-hidden />
                      {t('season.moved')}
                    </span>
                  )}
                </div>
              )}
            </>
          )
        )}
      </Block>
      <div className="flex min-w-0 flex-col gap-4">
        {role && r && (
          <section
            className="relative isolate overflow-hidden rounded-[28px] p-5 md:p-6"
            style={{
              backgroundImage: grad(ROLE_ART[role].tone),
              color: GRAD[ROLE_ART[role].tone].ink,
            }}
          >
            <SubjectArt
              icon={ROLE_ART[role].subject}
              tone={ROLE_ART[role].tone}
              size={140}
              ghost
              className="pointer-events-none absolute -top-4 -right-4 -z-10"
            />
            <p className="text-[13px] font-medium">
              {ctx.kind === 'student'
                ? t('season.yourRole')
                : ctx.kind === 'guardian'
                  ? ctx.child.firstName
                  : ''}
            </p>
            <p
              className="mt-1 font-display text-[34px] leading-[40px] font-light tracking-[-0.035em]"
              style={{ color: INK }}
            >
              {r.name}
            </p>
            <p className="mt-1 text-[14px]">{r.does}</p>
          </section>
        )}
        <Teams seasonId={season.id} />
      </div>
    </div>
  );
}

/**
 * «Сезон класу»: the class's story — the map, the mission of the station with a task for each
 * team, and how the class is doing together. The homeroom teacher starts it and may move on.
 */
export function SeasonPage() {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const classId =
    ctx.kind === 'student' || ctx.kind === 'guardian'
      ? ctx.child.class?.id
      : ctx.kind === 'teacher'
        ? ctx.homeroomClass?.id
        : undefined;
  const lead = ctx.kind === 'teacher' && Boolean(ctx.homeroomClass);
  const season = useClassSeason(classId);
  const actions = useSeasonActions(classId);
  const story = season.data ? seasonStory(season.data.key) : undefined;

  if (!classId) {
    return (
      <div className="flex flex-col gap-4">
        <PageHead title={t('season.title')} />
        <Block>
          <div className="flex flex-col items-center py-10 text-center">
            <LifeArt kind="rocket" tone="lavender" size={88} />
            <p className="mt-3 text-[16px]" style={{ color: INK_SOFT }}>
              {t('season.noClass')}
            </p>
          </div>
        </Block>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      <PageHead
        title={story ? story.title : t('season.title')}
        note={
          story && season.data
            ? t('season.station', {
                n: Math.min(season.data.stage + 1, season.data.stages),
                total: season.data.stages,
              })
            : undefined
        }
        sub={story ? <span className="block max-w-2xl">{story.intro}</span> : undefined}
      />
      {season.isPending ? (
        <div className="h-[200px] animate-pulse rounded-[28px] bg-white" aria-busy />
      ) : season.data && story ? (
        <>
          <Block>
            <SeasonMap
              story={story}
              stage={season.data.finished ? story.stations.length : season.data.stage}
            />
          </Block>
          <Running season={season.data} story={story} lead={lead} classId={classId} />
        </>
      ) : (
        <Block>
          <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
            <LifeArt kind="rocket" tone="lavender" size={88} />
            <div className="flex min-w-0 flex-col gap-3">
              <p className="text-[16px]" style={{ color: INK_SOFT }}>
                {t('season.none')}
              </p>
              {lead &&
                SEASONS.map((s) => (
                  <div key={s.key} className="flex flex-col items-start gap-2">
                    <button
                      type="button"
                      disabled={actions.start.isPending}
                      onClick={() => actions.start.mutate(s)}
                      className={`${pillClass('dark')} disabled:opacity-35`}
                    >
                      {t('season.start', { title: s.title })}
                      <PillIcon icon={RocketLaunchIcon} />
                    </button>
                    <p className="text-[13px]" style={{ color: INK_FAINT }}>
                      {t('season.startHint')}
                    </p>
                  </div>
                ))}
            </div>
          </div>
        </Block>
      )}
    </div>
  );
}

/** A small card for the student's home: where the class is and what is mine to do. */
export function SeasonCard({
  classId,
  studentId,
  className = '',
}: {
  classId: string;
  studentId: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const season = useClassSeason(classId).data;
  const marks = useSeasonMarks(season?.id, season?.stage).data;
  const story = season ? seasonStory(season.key) : undefined;
  if (!season || !story) return null;
  const station = story.stations[Math.min(season.stage, story.stations.length - 1)];
  const role = season.roles[studentId];
  const done = marks?.includes(studentId) ?? false;
  return (
    <Link
      to="/season"
      className={`group flex items-center gap-4 rounded-[28px] bg-white p-5 ${className}`}
    >
      <LifeArt kind="rocket" tone="lavender" size={48} />
      <span className="min-w-0 flex-1">
        <span
          className="block truncate text-[16px] leading-[22px] font-medium"
          style={{ color: INK }}
        >
          {story.title}
        </span>
        <span className="block truncate text-[13px]" style={{ color: INK_SOFT }}>
          {season.finished
            ? t('season.finished')
            : [
                t('season.station', {
                  n: Math.min(season.stage + 1, season.stages),
                  total: season.stages,
                }),
                station?.title,
                role && roleOf(role)?.name,
              ]
                .filter(Boolean)
                .join(' · ')}
        </span>
        {!season.finished && (
          <span
            className="mt-1 block text-[13px] font-medium"
            style={{ color: done ? GRAD.mint.ink : '#D9471A' }}
          >
            {done ? t('season.doneAlready') : t('season.done')}
          </span>
        )}
      </span>
      <span className={`${ringButton} group-hover:bg-[var(--sh-dark)] group-hover:text-white`}>
        <ArrowUpRightIcon size={16} weight="bold" aria-hidden />
      </span>
    </Link>
  );
}
